'use client'

import ChatListPanel from '@/components/atendimento/chat-list/chat-list-panel'
import { usePermission } from '@/lib/hooks/use-permission'
import { useSidebarTheme } from '@/lib/hooks/use-sidebar-theme'
import { useChatDock } from '@/lib/providers/chat-dock-provider'
import AtendimentoChatPage from '@/pages/admin/atendimento/atendimentos/atendimento-chat-page'
import { useEffect } from 'react'
import {
    MdChevronLeft,
    MdChevronRight,
    MdClose,
    MdSupportAgent,
} from 'react-icons/md'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/store/hooks'

/**
 * Atendimento acoplado ao menu.
 *
 * Fechado, é só um trilho com a seta encostado no menu. Aberto, a lista de
 * conversas toma o lugar do menu — que fica reduzido a ícones — e acompanha a
 * pessoa por todas as telas, atualizando em tempo real. Escolher uma conversa
 * sobrepõe o atendimento inteiro à tela atual, sem navegar: fechar devolve
 * exatamente o que estava embaixo.
 *
 * A conversa é o componente real do atendimento (`AtendimentoChatPage`, via
 * `chatIdOverride`), não uma cópia enxuta — responder, anexar, transferir,
 * encerrar e tudo o mais funcionam igual à tela cheia.
 */
export function ChatDock() {
    const { isDockOpen, toggleDock, openChatId, openChat, closeChat } =
        useChatDock()
    const { canRead } = usePermission()
    const location = useLocation()
    const { user } = useAuth()
    const { theme: sidebarTheme, themeClass } = useSidebarTheme(user?.id)
    const isLightSidebar = sidebarTheme === 'light'

    const canSeeChats = canRead('atendimento', 'channels')
    // Na tela cheia de atendimentos a lista já é a própria tela: repetir seria
    // duas listas idênticas lado a lado.
    const isChatFullScreen = location.pathname.startsWith('/atendimento/chats')

    // Esc fecha a conversa sobreposta, não o dock: é o gesto esperado de quem
    // abriu algo por cima e quer o que estava embaixo de volta.
    useEffect(() => {
        if (openChatId === null) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeChat()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [openChatId, closeChat])

    if (!canSeeChats || isChatFullScreen) return null

    return (
        <>
            {/* Trilho: fica colado no menu e é o único ponto de entrada. A faixa
                INTEIRA é o botão — um alvo de 24px por toda a altura da tela erra
                menos que um ícone de 20px, e não exige mirar. */}
            <button
                type="button"
                onClick={toggleDock}
                aria-expanded={isDockOpen}
                aria-label={
                    isDockOpen
                        ? 'Recolher os atendimentos'
                        : 'Abrir os atendimentos'
                }
                title={
                    isDockOpen
                        ? 'Recolher os atendimentos'
                        : 'Abrir os atendimentos'
                }
                className={`${themeClass} relative hidden lg:flex flex-shrink-0 w-4 h-screen border-r ${isLightSidebar ? 'border-base-300' : 'border-white/10'} items-center justify-center cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-inset`}
            >
                {/* O fundo é o mesmo da rail do menu (`.sidebar-dark` ou
                    `.sidebar-light`, conforme o tema da pessoa), então o trilho lê
                    como continuação dele e não como uma faixa avulsa. O realce do
                    hover vai por cima em cor translúcida: uma cor sólida taparia o
                    gradiente e cortaria a emenda justamente no movimento do mouse. */}
                <span className="absolute inset-0 bg-base-content/0 group-hover:bg-base-content/10 transition-colors" />
                {isDockOpen ? (
                    <MdChevronLeft className="relative text-sm text-base-content/60 group-hover:text-base-content transition-colors" />
                ) : (
                    <MdChevronRight className="relative text-sm text-base-content/60 group-hover:text-base-content transition-colors" />
                )}
            </button>

            {/* A lista, no lugar do menu. */}
            {isDockOpen && (
                <ChatListPanel
                    activeChatId={openChatId}
                    onSelectChat={openChat}
                    filterStorage="memory"
                    header={
                        <div className="flex items-center gap-2">
                            <MdSupportAgent className="text-lg text-secondary" />
                            <h2 className="text-base font-bold text-base-content">
                                Atendimentos
                            </h2>
                        </div>
                    }
                    className="hidden lg:flex w-[360px] flex-shrink-0 border-r border-base-300/50 h-screen"
                />
            )}

            {/* A conversa, por cima da tela atual. */}
            {isDockOpen && openChatId !== null && (
                <div className="hidden lg:flex fixed inset-y-0 right-0 left-[calc(4rem+1rem+360px)] z-40 flex-col bg-base-100 shadow-2xl border-l border-base-300/50">
                    <div className="flex-shrink-0 flex items-center justify-end px-3 py-1.5 border-b border-base-300/30 bg-base-200/40">
                        <button
                            type="button"
                            onClick={closeChat}
                            className="btn btn-sm btn-ghost gap-1"
                            title="Fechar a conversa e voltar à tela (Esc)"
                        >
                            <MdClose className="text-base" />
                            Fechar
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                        <AtendimentoChatPage
                            chatIdOverride={openChatId}
                            onBack={closeChat}
                        />
                    </div>
                </div>
            )}
        </>
    )
}
